// loader.js: the only file a site includes. Must be the first script in <head>,
// without async or defer, so its hooks are in place before the site's own code runs.
//
//   <script src="https://bugs.adido.dev/v0/loader.js" data-site="example-test" crossorigin="anonymous"></script>
//
// It captures console, errors, network and user actions into ring buffers straight
// away, shows the "Report a problem" button, loads replay.js once the page has
// loaded, and loads recorder.js when someone clicks the button.

import { ASSET_PATH, VERSION } from '../shared/constants.js';
import { serialise } from '../shared/serialise.js';
import { GROUPS } from '../shared/timeline.js';
import { createHost } from '../shared/ui-host.js';
import { isExcluded, readConfig } from './config.js';
import { createContext } from './context.js';
import { createRedactor } from './redact.js';
import { getSessionStorage, loadTimeline, saveTimeline, startSession } from './session.js';
import { mountButton } from './button.js';
import { installConsole } from './hooks/console.js';
import { installErrors } from './hooks/errors.js';
import { installFetch } from './hooks/fetch.js';
import { installXhr } from './hooks/xhr.js';
import { installBeacon } from './hooks/beacon.js';
import { installEventSource, installWebSocket } from './hooks/websocket.js';
import { installResources } from './hooks/resources.js';
import { installCsp } from './hooks/csp.js';
import { installActions } from './hooks/actions.js';
import { installNavigation } from './hooks/navigation.js';

function findScript() {
  const current = document.currentScript;
  if (current && current.src) return current;
  // currentScript is null if the loader was injected as a module or by a tag manager.
  return (
    document.querySelector('script[src*="/loader.js"][data-site]') ||
    document.querySelector('script[src*="/loader.js"]')
  );
}

function onReady(fn) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    fn();
  }
}

/** Runs fn after the load event, when the browser is idle, so replay never slows page load. */
function afterLoadIdle(fn) {
  const schedule = () => {
    if (typeof requestIdleCallback === 'function') requestIdleCallback(fn, { timeout: 2000 });
    else setTimeout(fn, 200);
  };
  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
}

function install(ctx, name, fn) {
  try {
    fn();
  } catch (error) {
    // One broken hook must not stop the others.
    ctx.push('custom', 'warn', `Adido bug capture: ${name} hook failed to start`, {
      error: String(error && error.message),
    });
  }
}

function main() {
  const existing = window.AdidoBugs;
  if (existing && existing.__loaded) return; // included twice

  const script = findScript();
  const src = script ? script.src : '';
  const windowConfig = existing && typeof existing === 'object' ? existing : {};
  const config = readConfig({ dataset: script ? script.dataset : {}, src, windowConfig });

  const disabledApi = (reason) => ({
    __loaded: true,
    version: VERSION,
    disabled: reason,
    metadata() {},
    event() {},
    open() {},
  });

  if (isExcluded(config, location.pathname)) {
    window.AdidoBugs = disabledApi('This path is excluded in the settings');
    return;
  }

  let assetBase = config.assets;
  if (!assetBase && src) assetBase = new URL(ASSET_PATH, src).href;
  if (assetBase && !assetBase.endsWith('/')) assetBase += '/';

  const storage = getSessionStorage();
  const session = startSession(storage, Date.now(), createRedactor(config).url);
  const ctx = createContext(config, { assetBase: assetBase || '', session });

  // Entries from earlier pages in this tab come first.
  const previous = loadTimeline(storage);
  for (const group of GROUPS) ctx.buffers[group].prepend(previous[group]);

  const capture = config.capture;
  if (capture.console) install(ctx, 'console', () => installConsole(ctx));
  if (capture.errors) install(ctx, 'errors', () => installErrors(ctx));
  if (capture.network) {
    install(ctx, 'fetch', () => installFetch(ctx));
    install(ctx, 'xhr', () => installXhr(ctx));
    install(ctx, 'beacon', () => installBeacon(ctx));
  }
  if (capture.websocket) {
    install(ctx, 'websocket', () => installWebSocket(ctx));
    install(ctx, 'eventsource', () => installEventSource(ctx));
  }
  if (capture.resources) install(ctx, 'resources', () => installResources(ctx));
  if (capture.csp) install(ctx, 'csp', () => installCsp(ctx));
  if (capture.actions) install(ctx, 'actions', () => installActions(ctx));
  if (capture.navigation) install(ctx, 'navigation', () => installNavigation(ctx));

  if (!config.site) {
    ctx.push('custom', 'warn', 'Adido bug capture: data-site is missing on the script tag', {});
  }

  const save = () => saveTimeline(storage, ctx.buffers);
  window.addEventListener('pagehide', save);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') save();
  });

  window.AdidoBugs = {
    __loaded: true,
    version: VERSION,
    config,
    /** Attach the site's own data (basket, user tier, build) to every report. Return a plain object under 10 KB. */
    metadata(fn) {
      ctx.metadataFn = typeof fn === 'function' ? fn : () => fn;
    },
    /** Add a custom marker to the timeline, such as "Checkout step 2". */
    event(name, detail) {
      ctx.push('custom', 'info', String(name), detail === undefined ? {} : { data: serialise(detail) });
    },
    /** Open the report panel from the site's own code. */
    open() {
      return ctx.open();
    },
    /** Internal: used by replay.js and recorder.js. */
    _ctx: ctx,
    _register(name, module) {
      ctx.modules[name] = module;
    },
  };

  onReady(() => {
    try {
      ctx.host = createHost({ accent: config.accent });
      if (config.button) mountButton(ctx);
      // Turbo, htmx and similar libraries swap the whole <body>, which removes our host.
      // Put it back, with its state (draft, recording) intact.
      setInterval(() => {
        const element = ctx.host && ctx.host.element;
        if (element && !element.isConnected) (document.body || document.documentElement).appendChild(element);
      }, 1000);
    } catch (error) {
      ctx.push('custom', 'warn', 'Adido bug capture: could not show the button', {
        error: String(error && error.message),
      });
    }
  });

  if (config.replay) {
    afterLoadIdle(() => {
      ctx.load('replay').catch((error) => {
        ctx.push('custom', 'warn', 'Adido bug capture: replay could not load', {
          error: String(error && error.message),
        });
      });
    });
  }
}

main();
